import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format, parseISO } from 'date-fns';
import { FileText, CheckCircle } from 'lucide-react';
import type { Entity, Facility, FacilityCreateRequest } from '@cortex/domain';

import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Separator } from '@/components/ui/shadcn/separator';
import { FormField } from '@/components/ui/FormField';
import { EmptyState } from '@/components/ui/EmptyState';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { formatMoney } from '@/components/ui/CurrencyValue';

/**
 * Detail pane for the Facilities tab.
 *
 * Three modes:
 *   - `idle`   — empty state with a "New facility" prompt.
 *   - `view`   — read-only display of all fields + "Activate" button when
 *                the facility is inactive AND the caller has facility.write.
 *   - `create` — form for a new agreement. Facilities are immutable once
 *                created — there is no edit mode. Version auto-increments
 *                server-side.
 *
 * `rateModifiers` is stored as `{}` on create in v1 and is not editable
 * through this UI.
 */

const V1_PRODUCTS = [{ slug: 'commercial-pa', label: 'Commercial PA' }];

const RISK_CLASSES = [
  'Risk 1 (Low)',
  'Risk 2 (Medium)',
  'Risk 3 (High)',
  'Risk 4 (Extreme)',
] as const;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/u;

const FacilityFormSchema = z
  .object({
    entityId: z.string().uuid('Select a brokerage entity.'),
    brokerageMinPct: z.number().min(0, 'Must be ≥ 0.').max(100, 'Must be ≤ 100.'),
    brokerageMaxPct: z.number().min(0, 'Must be ≥ 0.').max(100, 'Must be ≤ 100.'),
    canBind: z.boolean(),
    bindLimitB: z.number().positive('Must be a positive amount.').nullable().optional(),
    discretionaryRangePct: z.number().min(0, 'Must be ≥ 0.').max(100, 'Must be ≤ 100.'),
    validFrom: z.string().regex(DATE_RE, 'Use YYYY-MM-DD format.'),
    validTo: z.string().regex(DATE_RE, 'Use YYYY-MM-DD format.'),
    reviewDate: z.string().regex(DATE_RE, 'Use YYYY-MM-DD format.').optional().or(z.literal('')),
    notes: z.string().max(2000, 'Max 2 000 characters.'),
    productScope: z.array(z.string()).min(1, 'Select at least one product.'),
    allowedRiskClasses: z.array(z.string()).min(1, 'Select at least one risk class.'),
  })
  .refine((d) => d.brokerageMinPct <= d.brokerageMaxPct, {
    message: 'Minimum % must be ≤ maximum %.',
    path: ['brokerageMinPct'],
  })
  .refine((d) => d.validFrom < d.validTo, {
    message: 'Valid from must be before valid to.',
    path: ['validFrom'],
  })
  .refine((d) => !d.canBind || (d.bindLimitB !== null && d.bindLimitB !== undefined), {
    message: 'Bind limit (Limit B) is required when binding authority is enabled.',
    path: ['bindLimitB'],
  });

type FacilityFormValues = z.infer<typeof FacilityFormSchema>;

export interface FacilityDetailProps {
  readonly mode: 'idle' | 'view' | 'create';
  readonly facility: Facility | null;
  readonly entities: readonly Entity[];
  readonly isSaving: boolean;
  readonly isActivating: boolean;
  readonly canWrite: boolean;
  readonly errorMessage?: string | null;
  readonly onCreate: (input: FacilityCreateRequest) => Promise<void>;
  readonly onActivate: (id: string) => Promise<void>;
  readonly onCancel: () => void;
}

const emptyForm = (): FacilityFormValues => ({
  entityId: '',
  brokerageMinPct: 5,
  brokerageMaxPct: 15,
  canBind: false,
  bindLimitB: null,
  discretionaryRangePct: 10,
  validFrom: '',
  validTo: '',
  reviewDate: '',
  notes: '',
  productScope: ['commercial-pa'],
  allowedRiskClasses: ['Risk 1 (Low)', 'Risk 2 (Medium)'],
});

const formatDate = (iso: string) => {
  try {
    return format(parseISO(iso), 'dd MMM yyyy');
  } catch {
    return iso;
  }
};

const fmtPct = (s: string) => `${parseFloat(s).toFixed(2)}%`;
const fmtMoney = (s: string | null) => (s ? formatMoney(parseFloat(s)) : '—');

export function FacilityDetail({
  mode,
  facility,
  entities,
  isSaving,
  isActivating,
  canWrite,
  errorMessage,
  onCreate,
  onActivate,
  onCancel,
}: FacilityDetailProps) {
  const brokerageEntities = entities.filter((e) => e.entityTypeSlug === 'brokerage');

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    formState: { errors },
  } = useForm<FacilityFormValues>({
    resolver: zodResolver(FacilityFormSchema),
    defaultValues: emptyForm(),
  });

  useEffect(() => {
    if (mode === 'create') reset(emptyForm());
  }, [mode, reset]);

  const canBind = watch('canBind');

  const onSubmit = handleSubmit(async (values) => {
    const input: FacilityCreateRequest = {
      entityId: values.entityId,
      productScope: values.productScope,
      brokerageMinPct: values.brokerageMinPct,
      brokerageMaxPct: values.brokerageMaxPct,
      canBind: values.canBind,
      bindLimitB: values.canBind ? (values.bindLimitB ?? null) : null,
      allowedRiskClasses: values.allowedRiskClasses,
      discretionaryRangePct: values.discretionaryRangePct,
      validFrom: values.validFrom,
      validTo: values.validTo,
      reviewDate: values.reviewDate && values.reviewDate.length > 0 ? values.reviewDate : null,
      notes: values.notes,
    };
    await onCreate(input);
  });

  if (mode === 'idle') {
    return (
      <div className="flex h-full items-center justify-center">
        <EmptyState
          icon={FileText}
          title="Select a facility"
          description={
            canWrite
              ? 'Pick a facility from the list, or create a new agreement.'
              : 'Pick a facility from the list to view its details.'
          }
        />
      </div>
    );
  }

  if (mode === 'view' && facility) {
    return (
      <div className="flex h-full flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border bg-card px-6 py-4">
          <div>
            <h2 className="text-sm font-bold text-foreground">{facility.entityName}</h2>
            <p className="text-xs text-muted-foreground">
              Facility v{facility.version} · Created {formatDate(facility.createdAt)}
            </p>
          </div>
          <StatusBadge
            tone={facility.isActive ? 'active' : 'neutral'}
            label={facility.isActive ? 'Active' : 'Inactive'}
          />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {/* Terms */}
          <Section title="Agreement Terms">
            <ReadRow label="Products" value={facility.productScope.join(', ')} />
            <ReadRow
              label="Brokerage range"
              value={`${fmtPct(facility.brokerageMinPct)} – ${fmtPct(facility.brokerageMaxPct)}`}
            />
            <ReadRow label="Discretionary range" value={fmtPct(facility.discretionaryRangePct)} />
            <ReadRow label="Allowed risk classes" value={facility.allowedRiskClasses.join(', ')} />
          </Section>

          <Separator />

          {/* Binding authority */}
          <Section title="Binding Authority">
            <ReadRow label="Can bind" value={facility.canBind ? 'Yes' : 'No'} />
            {facility.canBind && (
              <ReadRow label="Bind limit (Limit B)" value={fmtMoney(facility.bindLimitB)} />
            )}
          </Section>

          <Separator />

          {/* Validity */}
          <Section title="Validity">
            <ReadRow label="Valid from" value={formatDate(facility.validFrom)} />
            <ReadRow label="Valid to" value={formatDate(facility.validTo)} />
            {facility.reviewDate && (
              <ReadRow label="Review date" value={formatDate(facility.reviewDate)} />
            )}
          </Section>

          {facility.notes && (
            <>
              <Separator />
              <Section title="Notes">
                <p className="text-xs text-muted-foreground whitespace-pre-wrap">
                  {facility.notes}
                </p>
              </Section>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border bg-card px-6 py-4 flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={onCancel}>
            Close
          </Button>
          {canWrite && !facility.isActive && (
            <Button size="sm" onClick={() => void onActivate(facility.id)} disabled={isActivating}>
              <CheckCircle className="h-4 w-4 mr-1.5" aria-hidden="true" />
              {isActivating ? 'Activating…' : 'Activate this version'}
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (mode === 'create') {
    return (
      <div className="flex h-full flex-col overflow-hidden">
        {/* Header */}
        <div className="border-b border-border bg-card px-6 py-4">
          <h2 className="text-sm font-bold text-foreground">New facility agreement</h2>
          <p className="text-xs text-muted-foreground">
            Facilities are immutable once created. A new version will be assigned automatically.
          </p>
        </div>

        {/* Form */}
        <form
          noValidate
          onSubmit={(e) => void onSubmit(e)}
          className="flex flex-1 flex-col overflow-hidden"
        >
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
            {errorMessage && (
              <div
                className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-xs text-destructive"
                role="alert"
              >
                {errorMessage}
              </div>
            )}

            {/* Entity */}
            <Section title="Brokerage">
              <FormField label="Brokerage entity" required error={errors.entityId?.message}>
                <select
                  {...register('entityId')}
                  className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs shadow-xs"
                >
                  <option value="">Select brokerage…</option>
                  {brokerageEntities.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name}
                    </option>
                  ))}
                </select>
              </FormField>
            </Section>

            <Separator />

            {/* Products */}
            <Section title="Product scope">
              <div className="space-y-2">
                {V1_PRODUCTS.map((p) => (
                  <label key={p.slug} className="flex items-center gap-2 text-xs cursor-pointer">
                    <Controller
                      control={control}
                      name="productScope"
                      render={({ field }) => (
                        <input
                          type="checkbox"
                          checked={field.value.includes(p.slug)}
                          disabled
                          onChange={() => undefined}
                          className="rounded border-input"
                        />
                      )}
                    />
                    {p.label}
                    <span className="text-muted-foreground text-xs">(v1 only product)</span>
                  </label>
                ))}
                {errors.productScope && (
                  <p className="text-xs text-destructive">{errors.productScope.message}</p>
                )}
              </div>
            </Section>

            <Separator />

            {/* Brokerage % */}
            <Section title="Brokerage range">
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Min brokerage %" required error={errors.brokerageMinPct?.message}>
                  <Input
                    {...register('brokerageMinPct', { valueAsNumber: true })}
                    type="number"
                    step="0.01"
                    min={0}
                    max={100}
                    className="h-8 text-xs"
                  />
                </FormField>
                <FormField label="Max brokerage %" required error={errors.brokerageMaxPct?.message}>
                  <Input
                    {...register('brokerageMaxPct', { valueAsNumber: true })}
                    type="number"
                    step="0.01"
                    min={0}
                    max={100}
                    className="h-8 text-xs"
                  />
                </FormField>
              </div>
              <FormField
                label="Discretionary range %"
                required
                hint="Maximum loading/discount the broker may apply."
                error={errors.discretionaryRangePct?.message}
              >
                <Input
                  {...register('discretionaryRangePct', { valueAsNumber: true })}
                  type="number"
                  step="0.01"
                  min={0}
                  max={100}
                  className="h-8 text-xs"
                />
              </FormField>
            </Section>

            <Separator />

            {/* Risk classes */}
            <Section title="Allowed risk classes">
              <div className="space-y-2">
                {RISK_CLASSES.map((rc) => (
                  <label key={rc} className="flex items-center gap-2 text-xs cursor-pointer">
                    <Controller
                      control={control}
                      name="allowedRiskClasses"
                      render={({ field }) => (
                        <input
                          type="checkbox"
                          checked={field.value.includes(rc)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              field.onChange([...field.value, rc]);
                            } else {
                              field.onChange(field.value.filter((v) => v !== rc));
                            }
                          }}
                          className="rounded border-input"
                        />
                      )}
                    />
                    {rc}
                  </label>
                ))}
                {errors.allowedRiskClasses && (
                  <p className="text-xs text-destructive">{errors.allowedRiskClasses.message}</p>
                )}
              </div>
            </Section>

            <Separator />

            {/* Binding authority */}
            <Section title="Binding authority">
              <label
                htmlFor="canBind"
                className="flex items-center gap-2 text-xs cursor-pointer font-medium"
              >
                <Controller
                  control={control}
                  name="canBind"
                  render={({ field }) => (
                    <input
                      id="canBind"
                      type="checkbox"
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      className="rounded border-input"
                    />
                  )}
                />
                Enable binding authority (SeniorBroker may bind quotes)
              </label>
              {canBind && (
                <FormField
                  label="Bind limit (Limit B) ZAR"
                  required
                  hint="Maximum gross premium a SeniorBroker may bind."
                  error={errors.bindLimitB?.message}
                >
                  <Input
                    {...register('bindLimitB', { valueAsNumber: true })}
                    type="number"
                    step="1"
                    min={1}
                    className="h-8 text-xs"
                    placeholder="e.g. 5000000"
                  />
                </FormField>
              )}
            </Section>

            <Separator />

            {/* Validity */}
            <Section title="Validity period">
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Valid from" required error={errors.validFrom?.message}>
                  <Input {...register('validFrom')} type="date" className="h-8 text-xs" />
                </FormField>
                <FormField label="Valid to" required error={errors.validTo?.message}>
                  <Input {...register('validTo')} type="date" className="h-8 text-xs" />
                </FormField>
              </div>
              <FormField
                label="Review date"
                hint="Optional reminder date for facility renewal."
                error={errors.reviewDate?.message}
              >
                <Input {...register('reviewDate')} type="date" className="h-8 text-xs" />
              </FormField>
            </Section>

            <Separator />

            {/* Notes */}
            <Section title="Notes">
              <FormField
                label="Compliance notes"
                error={errors.notes?.message}
                hint="Max 2 000 characters."
              >
                <textarea
                  {...register('notes')}
                  rows={4}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-xs shadow-xs focus:outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40 resize-none"
                  placeholder="Optional notes for compliance and operations…"
                />
              </FormField>
            </Section>
          </div>

          {/* Footer */}
          <div className="border-t border-border bg-card px-6 py-4 flex items-center justify-between">
            <Button type="button" variant="outline" size="sm" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Create facility'}
            </Button>
          </div>
        </form>
      </div>
    );
  }

  return null;
}

// ─── sub-components ──────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <h3 className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{title}</h3>
      {children}
    </div>
  );
}

function ReadRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-xs text-muted-foreground shrink-0">{label}</span>
      <span className="text-xs font-medium text-foreground text-right">{value}</span>
    </div>
  );
}
